import { useMemo } from 'react';
import type { ChartData, ChartOptions, FontSpec } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { horizontalLine } from '@/components/charts/barChartUtils';
import type { BarChartConfig } from '@/components/charts/chartConfig';
import { defaultChartFontFamily, remToPixels } from '@/components/charts/chartFont';
import { configuredOr } from '@/lib/configValue';
import '@/components/charts/register';

/**
 * The pentagon bar chart, shared by `HourlyChartWidget`. Port of `components/charts/barChart.vue`.
 *
 * The drawing itself is `SigangeBarController` in `barChartUtils.ts`; this file is the option
 * builder that sat in the Vue component's `setup()`, ported key for key including which
 * fallback test each key uses (`!== undefined` for some, truthiness for others — the legacy
 * code is not consistent and the difference is visible for a configured `0`).
 */

export type BarChartProps = {
  /** One value per configured label. Zeros before the first message. */
  values: number[];
  config: BarChartConfig;
  className?: string;
};

type BarChartOptions = ChartOptions<'SigangeBarController'>;
type BarScaleOptions = NonNullable<BarChartOptions['scales']>;
type BarTitleOptions = NonNullable<NonNullable<BarChartOptions['plugins']>['title']>;

export function BarChart({ values, config, className }: BarChartProps) {
  const data = useMemo<ChartData<'SigangeBarController', number[]>>(
    () => ({
      labels: config.labels,
      datasets: [
        {
          data: values,
          backgroundColor: config.backgroundColor,
          borderColor: config.borderColor,
          borderWidth: 0,
          maxBarThickness: 25,
        },
      ],
    }),
    [values, config],
  );

  const options = useMemo<BarChartOptions>(
    () => ({
      maintainAspectRatio: false,
      scales: buildScales(config),
      responsive: true,
      plugins: {
        legend: {
          display: false,
        },
        title: buildTitle(config),
        /**
         * Inert, and carried over anyway.
         *
         * `chartjs-plugin-datalabels` is never registered globally (see `register.ts`) and this
         * chart does not pass it inline, so this block has had no effect in the legacy app
         * either — the numbers above the bars are painted by `SigangeBarController.draw()` from
         * `barTickOption`. It is kept because it is what the deployed configs' rendering was
         * specified against, and dropping it would make a future global registration change the
         * chart silently rather than visibly.
         */
        datalabels: {
          font(context) {
            const width = context.chart.width || 320;
            const size = Math.round(width / 32);
            return { size, weight: 600 };
          },
          formatter(value: number) {
            if (value === 0) {
              return '';
            }
            return value;
          },
        },
        // The channel that replaces the legacy top-level `barTickOption` config key — see
        // forced change #1 in `barChartUtils.ts`.
        signageBarTick: config.barTickOption,
      },
    }),
    [config],
  );

  /**
   * The threshold-line plugin is built once per config and never rebuilt: `react-chartjs-2`
   * hands `plugins` to the `Chart` constructor and never reads it again, so a plugin rebuilt
   * on a data change would be silently ignored. Everything it draws comes from config, which
   * does not change for the lifetime of the page.
   */
  const plugins = useMemo(
    () => (config.horizontalLine !== undefined ? [horizontalLine(config.horizontalLine)] : []),
    [config.horizontalLine],
  );

  const maxHeight = config.maxHeight !== undefined ? `${String(config.maxHeight)}rem` : '10rem';

  return (
    <Chart
      type="SigangeBarController"
      data={data}
      options={options}
      plugins={plugins}
      className={className}
      style={{ height: maxHeight, maxHeight }}
    />
  );
}

/** `chartConfig.title` -> Chart.js's title plugin. An absent or empty title yields `{}`. */
function buildTitle(config: BarChartConfig): BarTitleOptions {
  if (!config.title?.titletText) return {};

  const { titletText, fontSize, fontFamily, fontWeight, fontColor } = config.title;
  return {
    display: true,
    text: titletText,
    color: configuredOr(fontColor, 'white'),
    font: {
      // [sic] The legacy default is `10` — ten *rem*, ~190px at the `lg` root size. Every
      // deployed config sets `fontSize`, so the default has never been rendered.
      size: remToPixels(configuredOr(fontSize, 10)),
      family: configuredOr(fontFamily, defaultChartFontFamily()),
      weight: toChartFontWeight(fontWeight),
    },
  };
}

/**
 * `fontWeight` from config -> Chart.js's `FontSpec['weight']`.
 *
 * CSS accepts any weight keyword or number; Chart.js's typings accept a number or one of four
 * keywords. The config value is free-form text from a JSON file and is passed through
 * unchanged, exactly as the legacy code did — Chart.js writes it straight into `ctx.font`,
 * where an unrecognised weight is dropped by the canvas rather than rejected. The cast is what
 * says that out loud; narrowing here would mean silently rewriting a configured weight.
 */
function toChartFontWeight(weight: string | undefined): FontSpec['weight'] {
  return configuredOr(weight, 'normal') as FontSpec['weight'];
}

/**
 * `chartConfig.grid` -> Chart.js scale options.
 *
 * Legacy built this axis by axis with the same block copy-pasted twice; the only genuine
 * difference is that `y` also carries `suggestedMin`/`suggestedMax`. An absent `grid` yields
 * `{}`, which lets Chart.js pick its own scales — the legacy behaviour, and not one any
 * deployed config exercises.
 */
function buildScales(config: BarChartConfig): BarScaleOptions {
  const scales: BarScaleOptions = {};
  if (!config.grid) return scales;

  const { x, y } = config.grid;

  if (x) {
    scales.x = {
      beginAtZero: true,
      grid: {
        display: x.display ?? true,
        color: configuredOr(x.color, 'grey'),
      },
      // [chart.js 4] **Forced change #4.** Chart.js 3's `scales.<id>.grid.drawBorder` was
      // removed in 4 and replaced by `scales.<id>.border.display`. Passing the old key through
      // would be silently ignored, and since `border.display` defaults to **true**, all four
      // deployed configs — every one of which sets `drawBorder: false` on both axes — would
      // grow a pair of axis lines the legacy app does not draw.
      border: { display: x.drawBorder ?? true },
      ticks: {
        display: x.ticks.display ?? true,
        color: x.ticks.color ?? 'grey',
        font: tickFont(x.ticks.font),
      },
    };
  }

  if (y) {
    scales.y = {
      beginAtZero: true,
      grid: {
        display: y.display ?? true,
        color: configuredOr(y.color, 'grey'),
      },
      border: { display: y.drawBorder ?? true },
      ticks: {
        display: y.ticks.display ?? true,
        color: y.ticks.color ?? 'grey',
        font: tickFont(y.ticks.font),
      },
      suggestedMin: 0,
      suggestedMax: configuredOr(y.suggestedMax, 0),
    };
  }

  return scales;
}

/**
 * A tick `font` block, or `{}` when the config has none — in which case Chart.js's own font
 * defaults apply, which is what the legacy `: {}` branch produced too.
 *
 * The `15` fallback is pixels, not rem: legacy only converted the configured value.
 */
function tickFont(
  font: { size?: number; family?: string; weight?: string } | undefined,
): Partial<FontSpec> {
  if (!font) return {};
  return {
    size: font.size ? remToPixels(font.size) : 15,
    family: configuredOr(font.family, defaultChartFontFamily()),
    weight: toChartFontWeight(font.weight),
  };
}
